import { useLanguage } from '@/context/LanguageContext';
import { Link } from 'react-router-dom';
import { Handshake, Globe, TrendingUp, Users, Building2, ArrowRight, Flag } from 'lucide-react';

const FLAGS_IMAGE = 'https://images.unsplash.com/photo-1668734593713-4b5bdc4faab6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxkaXBsb21hdGljJTIwZmxhZ3MlMjBtZWV0aW5nfGVufDB8fHx8MTc3NTUxMjMzMHww&ixlib=rb-4.1.0&q=85';

export default function AngolaSpain() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-white" data-testid="angola-spain-page">
      {/* Hero */}
      <div className="relative h-[340px] md:h-[400px] overflow-hidden">
        <img src={FLAGS_IMAGE} alt="Angola Espanha" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FFD700] mb-3">{t('angola_spain.overline')}</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight font-serif">{t('angola_spain.title')}</h1>
          <p className="text-base text-white/80 max-w-2xl mt-4">{t('angola_spain.subtitle')}</p>
        </div>
      </div>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#CC0000] mb-3">{t('angola_spain.relations_overline')}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight font-serif mb-6">{t('angola_spain.relations_title')}</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>{t('angola_spain.relations_p1')}</p>
                <p>{t('angola_spain.relations_p2')}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '1975', label: t('angola_spain.stat_year'), icon: Flag },
                { value: '+50K', label: t('angola_spain.stat_community'), icon: Users },
                { value: '20+', label: t('angola_spain.stat_agreements'), icon: Handshake },
                { value: '5+', label: t('angola_spain.stat_sectors'), icon: TrendingUp },
              ].map(({ value, label, icon: Icon }) => (
                <div key={label} className="bg-gray-50 border border-gray-100 rounded-md p-6 text-center hover:shadow-sm transition-all">
                  <Icon size={24} className="text-[#CC0000] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{value}</p>
                  <p className="text-xs text-gray-500 mt-1">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Areas */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight font-serif">{t('angola_spain.areas_title')}</h2>
            <div className="w-16 h-1 bg-[#CC0000] mx-auto mt-4" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: TrendingUp, title: t('angola_spain.area_economy'), desc: t('angola_spain.area_economy_desc') },
              { icon: Globe, title: t('angola_spain.area_culture'), desc: t('angola_spain.area_culture_desc') },
              { icon: Users, title: t('angola_spain.area_education'), desc: t('angola_spain.area_education_desc') },
              { icon: Building2, title: t('angola_spain.area_energy'), desc: t('angola_spain.area_energy_desc') },
              { icon: Handshake, title: t('angola_spain.area_politics'), desc: t('angola_spain.area_politics_desc') },
              { icon: Flag, title: t('angola_spain.area_tourism'), desc: t('angola_spain.area_tourism_desc') },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white p-6 border border-gray-100 rounded-md hover:shadow-md hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-4">
                  <Icon size={20} className="text-[#CC0000]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white font-serif mb-4">{t('angola_spain.cta_title')}</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">{t('angola_spain.cta_desc')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/investir-em-angola" className="inline-flex items-center gap-2 px-6 py-3 bg-[#CC0000] text-white font-medium rounded-sm hover:bg-[#A30000] transition-colors" data-testid="cta-invest-btn">
              {t('angola_spain.cta_invest')} <ArrowRight size={16} />
            </Link>
            <Link to="/contactos" className="inline-flex items-center gap-2 px-6 py-3 border-2 border-white/30 text-white font-medium rounded-sm hover:bg-white/10 transition-colors" data-testid="cta-contact-btn">
              {t('angola_spain.cta_contact')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
